/* ============================================================
   INEXORABLE — theme.js
   ============================================================ */
(function () {
  'use strict';

  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const CFG = window.InexorableConfig || {};

  // On a localized storefront every route carries a market prefix (/fr-fr/).
  // Shopify exposes it here; it always ends with a slash.
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const route = path => ROOT + path.replace(/^\//, '');

  /* ---------- Money ---------- */
  function activeCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  }
  function currencyRate() {
    const r = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1);
    return isNaN(r) ? 1 : r;
  }
  // cents already in the presentment currency
  function money(cents) {
    try {
      return new Intl.NumberFormat(CFG.locale || 'fr-FR', {
        style: 'currency',
        currency: activeCurrency(),
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + activeCurrency();
    }
  }
  // EUR cents converted to the presentment currency
  function moneyFromEur(eurCents) {
    return money(Math.round(eurCents * currencyRate()));
  }

  // The predictive-search API returns price as a string that may be either
  // major units ("49.00") or cents ("4900") depending on the shop.
  function moneyFromSuggest(raw) {
    if (raw == null || raw === '') return '';
    const s = String(raw);
    return money(s.indexOf('.') > -1 ? Math.round(parseFloat(s) * 100) : parseInt(s, 10));
  }

  /* ---------- Toast ---------- */
  let toastEl, toastTimer;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    requestAnimationFrame(() => toastEl.classList.add('toast--on'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('toast--on'), 2600);
  }

  /* ---------- Session-seeded shuffle ----------
     Same visitor keeps the same order while browsing,
     a new session reshuffles the selection.               */
  function sessionSeed() {
    let s = null;
    try { s = sessionStorage.getItem('inx_seed'); } catch (e) {}
    if (!s) {
      s = String(Date.now() ^ Math.floor(Math.random() * 0xffffffff));
      try { sessionStorage.setItem('inx_seed', s); } catch (e) {}
    }
    return parseInt(s, 10) >>> 0;
  }
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function seededShuffle(arr, seed) {
    const rnd = mulberry32(seed);
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function initShuffle() {
    $$('[data-shuffle]').forEach(container => {
      const limit = parseInt(container.dataset.shuffleLimit || '20', 10);
      const items = Array.from(container.children);
      if (items.length < 2) return;
      const ordered = seededShuffle(items, sessionSeed());
      const frag = document.createDocumentFragment();
      ordered.forEach((el, i) => {
        if (i < limit) { el.hidden = false; frag.appendChild(el); }
        else { el.remove(); }
      });
      container.appendChild(frag);
      container.removeAttribute('data-shuffle');
    });
  }

  /* ---------- Horizontal rails ---------- */
  function initRails() {
    $$('.rail-wrap').forEach(wrap => {
      if (wrap.dataset.railReady) return;
      const rail = $('.rail', wrap);
      const prev = $('.rail-nav--prev', wrap);
      const next = $('.rail-nav--next', wrap);
      if (!rail || !prev || !next) return;
      wrap.dataset.railReady = '1';

      const step = () => rail.clientWidth * 0.8;
      const sync = () => {
        // An auto-scrolling rail loops forever, so it has no start or end
        if (rail.dataset.autoscroll) { prev.disabled = false; next.disabled = false; return; }
        prev.disabled = rail.scrollLeft < 8;
        next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 8;
      };
      prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
      rail.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* ---------- Auto-scrolling rail ----------
     Continuous pixel-by-pixel drift rather than timed jumps, so it reads as
     motion instead of a slideshow. The item set is cloned once and the scroll
     position wraps at the halfway mark, which makes the loop seamless.      */
  const AUTOSCROLL_SPEED = { slow: 0.25, medium: 0.5, fast: 0.9 };

  function startAutoScroll(rail) {
    const speed = AUTOSCROLL_SPEED[rail.dataset.autoscroll] || AUTOSCROLL_SPEED.slow;

    Array.from(rail.children).forEach(el => {
      const clone = el.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      $$('a, button', clone).forEach(f => f.tabIndex = -1);
      rail.appendChild(clone);
    });

    let loopWidth = rail.scrollWidth / 2;
    let pos = 0;
    let hovered = false, offscreen = false;

    // The position is tracked here rather than read back from scrollLeft:
    // browsers may round that value to a whole pixel, and a 0.25px increment
    // would then be rounded away every frame — the rail would never move.
    const step = () => {
      if (!hovered && !offscreen && loopWidth > 0) {
        pos += speed;
        if (pos >= loopWidth) pos -= loopWidth;
        rail.scrollLeft = pos;
      }
      requestAnimationFrame(step);
    };

    // Listen on the wrapper, not the rail: that also covers the prev/next
    // arrows, whose smooth scrolling would otherwise fight the drift.
    const zone = rail.closest('.rail-wrap') || rail;
    zone.addEventListener('mouseenter', () => { hovered = true; });
    zone.addEventListener('mouseleave', () => {
      pos = rail.scrollLeft;   // pick up wherever the visitor left it
      hovered = false;
    });
    zone.addEventListener('focusin',  () => { hovered = true; });
    zone.addEventListener('focusout', () => { pos = rail.scrollLeft; hovered = false; });

    rail.addEventListener('touchstart', () => { hovered = true; }, { passive: true });
    rail.addEventListener('touchend', () => {
      setTimeout(() => { pos = rail.scrollLeft; hovered = false; }, 2500);
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        entries.forEach(en => { offscreen = !en.isIntersecting; });
      }).observe(rail);
    }

    // Card widths shift on resize, so the wrap point has to follow
    window.addEventListener('resize', () => {
      loopWidth = rail.scrollWidth / 2;
      if (pos > loopWidth) pos = 0;
    });

    requestAnimationFrame(step);
  }

  function initAutoScroll() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    $$('.rail[data-autoscroll]').forEach(rail => {
      if (rail.dataset.autoReady) return;

      // Widths may not be settled yet (fonts, images, late layout). Rather
      // than bail for good, keep checking briefly until the rail overflows.
      let tries = 0;
      const arm = () => {
        if (rail.dataset.autoReady) return;
        if (rail.scrollWidth > rail.clientWidth + 4) {
          rail.dataset.autoReady = '1';
          startAutoScroll(rail);
        } else if (++tries < 20) {
          setTimeout(arm, 250);
        }
      };
      arm();
    });
  }

  /* ---------- Site navigation panel ---------- */
  function initNav() {
    const burger = $('#Burger');
    const nav = $('#SiteNav');
    const overlay = $('#SiteNavOverlay');

    if (burger && nav) {
      const setNav = open => {
        burger.setAttribute('aria-expanded', String(open));
        nav.setAttribute('aria-hidden', String(!open));
        if (overlay) overlay.setAttribute('aria-hidden', String(!open));
        document.body.classList.toggle('no-scroll', open);
      };

      burger.addEventListener('click', () => {
        setNav(burger.getAttribute('aria-expanded') !== 'true');
      });
      overlay && overlay.addEventListener('click', () => setNav(false));
      $$('a', nav).forEach(a => a.addEventListener('click', () => setNav(false)));
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && burger.getAttribute('aria-expanded') === 'true') {
          setNav(false);
          burger.focus();
        }
      });
    }

    // Country / currency selectors
    $$('[data-localization-select]').forEach(sel => {
      sel.addEventListener('change', () => {
        const form = sel.closest('form');
        if (form) form.submit();
      });
    });
  }

  /* ---------- Search panel ---------- */
  function initSearch() {
    const panel = $('#SearchPanel');
    const openBtn = $('#SearchOpen');
    const closeBtn = $('#SearchClose');
    const input = $('#SearchInput');
    const out = $('#SearchResults');
    if (!panel) return;

    const open = () => {
      panel.setAttribute('aria-hidden', 'false');
      setTimeout(() => input && input.focus(), 120);
    };
    const close = () => panel.setAttribute('aria-hidden', 'true');

    openBtn && openBtn.addEventListener('click', () => {
      panel.getAttribute('aria-hidden') === 'false' ? close() : open();
    });
    closeBtn && closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });

    if (!input || !out) return;
    let timer, controller;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { out.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();
        try {
          const endpoint = route('search/suggest.json') + '?q=' + encodeURIComponent(q) +
                      '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
          const res = await fetch(endpoint, { signal: controller.signal });
          const data = await res.json();
          const products = (data.resources.results.products || []);
          if (!products.length) {
            out.innerHTML = '<p class="search-hint">Aucun résultat pour « ' + q.replace(/</g, '&lt;') + ' »</p>';
            return;
          }
          out.innerHTML =
            '<p class="search-hint">' + products.length + ' résultat' + (products.length > 1 ? 's' : '') + '</p>' +
            '<div class="grid">' + products.map(p => {
              const img = p.featured_image && p.featured_image.url
                ? '<img class="card__img card__img--main" src="' + p.featured_image.url + '&width=400" alt="" loading="lazy">'
                : '<div class="card__img card__img--main"></div>';
              return '<article class="card"><a class="card__media" href="' + p.url + '">' + img + '</a>' +
                '<div class="card__body"><a class="card__title" href="' + p.url + '">' + p.title + '</a>' +
                '<div class="card__meta"><span class="card__price">' + moneyFromSuggest(p.price) + '</span></div>' +
                '</div></article>';
            }).join('') + '</div>';
        } catch (e) { if (e.name !== 'AbortError') out.innerHTML = ''; }
      }, 240);
    });
  }

  /* ============================================================
     CART
     ============================================================ */

  const SHIPPING_ZONES = {
    'france-relay':    { label: 'France — Mondial Relay',    threshold: 15000 },
    'france-colissimo':{ label: 'France — Colissimo',        threshold: 20000 },
    'eu':              { label: 'Union Européenne',          threshold: 25000 },
    'europe-hors-ue':  { label: 'Europe (hors UE)',          threshold: 30000 },
    'international':   { label: 'International',             threshold: 50000 }
  };
  const EU = ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE'];
  const EUROPE_NON_EU = ['GB','CH','NO','IS','LI','AD','SM','VA','AL','BA','ME','MK','RS','XK','MD','UA','BY','TR','RU','GE','AM','AZ'];

  function zoneFromCountry(code) {
    if (!code) return null;
    code = code.toUpperCase();
    if (code === 'FR' || code === 'MC') return 'france-relay';
    if (EU.includes(code)) return 'eu';
    if (EUROPE_NON_EU.includes(code)) return 'europe-hors-ue';
    return 'international';
  }
  function detectedZone() {
    const country = CFG.country ||
                    (window.Shopify && window.Shopify.country) ||
                    ($('[data-localization-select]') && $('[data-localization-select]').value);
    return zoneFromCountry(country) || 'france-relay';
  }

  // The <select> always carries a value, so it can only be trusted once it
  // has been seeded from the shopper's country (or changed by hand).
  function currentZone() {
    const sel = $('#ShipZone');
    if (sel && sel.dataset.ready) return sel.value;
    return detectedZone();
  }

  function renderShipping(totalCents) {
    const wrap = $('#ShipProgress');
    if (!wrap) return;
    const zone = SHIPPING_ZONES[currentZone()] || SHIPPING_ZONES['france-relay'];
    const target = Math.round(zone.threshold * currencyRate());
    const remaining = target - totalCents;
    const textEl = $('#ShipText', wrap);
    const fillEl = $('#ShipFill', wrap);
    if (remaining > 0) {
      textEl.innerHTML = 'Plus que <b>' + money(remaining) + '</b> pour la livraison offerte';
    } else {
      textEl.innerHTML = '<b>Livraison offerte débloquée</b>';
    }
    fillEl.style.width = Math.min(100, (totalCents / target) * 100) + '%';
  }

  function cartItemHTML(item) {
    const img = item.image
      ? '<img class="citem__img" src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') + '" alt="" loading="lazy">'
      : '<div class="citem__img"></div>';
    const variant = (item.variant_title && item.variant_title !== 'Default Title')
      ? '<p class="citem__variant">' + item.variant_title + '</p>' : '';
    // Shopify caps the quantity at what is actually in stock, so a one-of-one
    // piece simply refuses to go above 1.
    const qtyCtl =
      '<div class="citem__qty">' +
        '<button type="button" data-key="' + item.key + '" data-qty="' + (item.quantity - 1) + '" aria-label="Diminuer">−</button>' +
        '<span>' + item.quantity + '</span>' +
        '<button type="button" data-key="' + item.key + '" data-qty="' + (item.quantity + 1) + '" aria-label="Augmenter">+</button>' +
      '</div>';

    return '<div class="citem" data-key="' + item.key + '">' +
      '<a href="' + item.url + '">' + img + '</a>' +
      '<div class="citem__main">' +
        '<div class="citem__top">' +
          '<div><a class="citem__title" href="' + item.url + '">' + item.product_title + '</a>' + variant + '</div>' +
          '<button class="citem__rm" data-remove="' + item.key + '" aria-label="Retirer du panier">' +
            '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
            '<polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>' +
            '<path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="citem__bot">' + qtyCtl +
          '<span class="citem__price">' + money(item.final_line_price) + '</span>' +
        '</div>' +
      '</div></div>';
  }

  async function renderUpsell(cart) {
    const wrap = $('#Upsell');
    if (!wrap) return;
    const handle = (CFG.upsellHandle || '').trim();
    if (!handle || !cart.items.length) { wrap.hidden = true; return; }
    if (cart.items.some(i => i.handle === handle)) { wrap.hidden = true; return; }
    try {
      const res = await fetch(route('products/' + handle + '.js'));
      if (!res.ok) throw new Error('not found');
      const p = await res.json();
      const v = p.variants.find(x => x.available) || p.variants[0];
      if (!v) throw new Error('no variant');
      const img = p.featured_image
        ? '<img class="upsell__img" src="' + p.featured_image + '&width=120" alt="" loading="lazy">'
        : '<div class="upsell__img"></div>';
      wrap.innerHTML =
        '<p class="upsell__head">Complétez votre panier</p>' +
        '<div class="upsell__row">' + img +
          '<div class="upsell__info"><p class="upsell__title">' + p.title + '</p>' +
          '<p class="upsell__price">' + money(v.price) + '</p></div>' +
          '<button class="upsell__btn" data-upsell-add="' + v.id + '">+ Ajouter</button>' +
        '</div>';
      wrap.hidden = false;
    } catch (e) { wrap.hidden = true; }
  }

  function renderCart(cart) {
    const body = $('#CartBody');
    const foot = $('#CartFoot');
    const count = $('#CartCount');

    if (count) {
      count.textContent = cart.item_count;
      count.style.display = cart.item_count > 0 ? 'grid' : 'none';
    }
    if (!body) return;

    if (!cart.items.length) {
      body.innerHTML = '<div class="cart-empty"><p>Votre panier est vide.</p>' +
        '<a href="' + route('collections/all') + '" class="btn btn--outline">Découvrir les pièces</a></div>';
      if (foot) foot.hidden = true;
      const up = $('#Upsell'); if (up) up.hidden = true;
      return;
    }

    body.innerHTML = cart.items.map(cartItemHTML).join('');
    if (foot) foot.hidden = false;
    const sub = $('#CartSubtotal');
    if (sub) sub.textContent = money(cart.total_price);
    renderShipping(cart.total_price);
    renderUpsell(cart);
  }

  async function fetchCart() {
    const res = await fetch(route('cart.js'));
    return res.json();
  }
  async function refreshCart() {
    try { renderCart(await fetchCart()); } catch (e) {}
  }

  async function changeLine(key, quantity) {
    const res = await fetch(route('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    renderCart(await res.json());
  }

  async function addToCart(variantId, quantity) {
    const res = await fetch(route('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1 }] })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Ajout impossible');
    }
    await refreshCart();
  }

  function openDrawer() {
    const d = $('#CartDrawer'), o = $('#CartOverlay');
    if (!d) return;
    d.setAttribute('aria-hidden', 'false');
    o && o.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
  }
  function closeDrawer() {
    const d = $('#CartDrawer'), o = $('#CartOverlay');
    if (!d) return;
    d.setAttribute('aria-hidden', 'true');
    o && o.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
  }

  function initCart() {
    $('#CartOpen') && $('#CartOpen').addEventListener('click', () => { openDrawer(); refreshCart(); });
    $('#CartClose') && $('#CartClose').addEventListener('click', closeDrawer);
    $('#CartOverlay') && $('#CartOverlay').addEventListener('click', closeDrawer);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });

    // Delegated cart interactions
    document.addEventListener('click', async e => {
      const rm = e.target.closest('[data-remove]');
      if (rm) { await changeLine(rm.dataset.remove, 0); return; }

      const q = e.target.closest('.citem__qty button[data-key]');
      if (q) { await changeLine(q.dataset.key, parseInt(q.dataset.qty, 10)); return; }

      const up = e.target.closest('[data-upsell-add]');
      if (up) {
        up.disabled = true; up.textContent = '...';
        try { await addToCart(up.dataset.upsellAdd, 1); toast('Ajouté au panier'); }
        catch (err) { toast(err.message); up.disabled = false; up.textContent = '+ Ajouter'; }
        return;
      }

      // Quick add straight from a product card size chip
      const chip = e.target.closest('[data-quick-add]');
      if (chip) {
        e.preventDefault();
        chip.style.opacity = '0.5';
        try { await addToCart(chip.dataset.quickAdd, 1); openDrawer(); toast('Ajouté au panier'); }
        catch (err) { toast(err.message); }
        chip.style.opacity = '';
        return;
      }
    });

    const zoneSel = $('#ShipZone');
    if (zoneSel) {
      const detected = detectedZone();
      if (zoneSel.querySelector('option[value="' + detected + '"]')) zoneSel.value = detected;
      zoneSel.dataset.ready = '1';
      zoneSel.addEventListener('change', () => fetchCart().then(c => renderShipping(c.total_price)));
    }

    refreshCart();
  }

  /* ============================================================
     PRODUCT PAGE
     ============================================================ */

  function initGallery() {
    const stage = $('#PStage');
    if (!stage) return;
    const slides = $$('.pslide', stage);
    const thumbs = $$('.pthumb');
    const counter = $('#PCounter');
    let idx = 0;

    const show = i => {
      idx = (i + slides.length) % slides.length;
      slides.forEach((s, n) => s.classList.toggle('pslide--on', n === idx));
      thumbs.forEach((t, n) => t.classList.toggle('pthumb--on', n === idx));
      if (counter) counter.textContent = (idx + 1) + ' / ' + slides.length;
      const active = thumbs[idx];
      if (active && active.scrollIntoView) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    };

    thumbs.forEach((t, n) => t.addEventListener('click', () => show(n)));
    $('#PPrev') && $('#PPrev').addEventListener('click', () => show(idx - 1));
    $('#PNext') && $('#PNext').addEventListener('click', () => show(idx + 1));

    document.addEventListener('keydown', e => {
      if (e.target.matches('input, textarea, select')) return;
      if (e.key === 'ArrowLeft') show(idx - 1);
      if (e.key === 'ArrowRight') show(idx + 1);
    });

    // Swipe
    let x0 = null;
    stage.addEventListener('touchstart', e => { x0 = e.touches[0].clientX; }, { passive: true });
    stage.addEventListener('touchend', e => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 45) show(dx < 0 ? idx + 1 : idx - 1);
      x0 = null;
    }, { passive: true });
  }

  function initProductForm() {
    const form = $('#ProductForm');
    if (!form) return;
    const data = window.__product;
    if (!data) return;

    const idField = $('#VariantId', form);
    const addBtn = $('#AddBtn', form);
    const addTxt = $('#AddTxt', form);
    const priceBox = $('#PPriceBox');
    const errBox = $('#PError');
    const qtyInput = $('#Qty', form);

    function selectedOptions() {
      return $$('input[data-opt-index]:checked', form)
        .sort((a, b) => a.dataset.optIndex - b.dataset.optIndex)
        .map(i => i.value);
    }

    // A product with no variant options renders no radios at all, so there is
    // nothing to match against — fall back to its single variant.
    function currentVariant() {
      const inputs = $$('input[data-opt-index]', form);
      if (!inputs.length) return data.variants[0] || null;

      const opts = selectedOptions();
      if (opts.length !== data.options.length) return null;

      return data.variants.find(v =>
        v.options.length === opts.length && v.options.every((o, i) => o === opts[i])
      ) || null;
    }

    function renderPrice(v) {
      if (!priceBox) return;
      if (v.compare_at_price && v.compare_at_price > v.price) {
        const off = Math.round((1 - v.price / v.compare_at_price) * 100);
        priceBox.innerHTML =
          '<span class="pprice__now">' + money(v.price) + '</span>' +
          '<span class="pprice__was">' + money(v.compare_at_price) + '</span>' +
          '<span class="pprice__off">-' + off + '%</span>';
      } else {
        priceBox.innerHTML = '<span class="pprice__now">' + money(v.price) + '</span>';
      }
    }

    function update() {
      const v = currentVariant();
      if (!v) {
        addBtn.disabled = true;
        addTxt.textContent = 'Indisponible';
        return;
      }
      idField.value = v.id;
      renderPrice(v);
      addBtn.disabled = !v.available;
      addTxt.textContent = v.available ? 'Ajouter au panier' : 'Épuisé';

      if (qtyInput) {
        const max = v.inventory_management === 'shopify' && v.inventory_policy === 'deny'
          ? Math.max(1, v.inventory_quantity) : 99;
        qtyInput.max = max;
        if (parseInt(qtyInput.value, 10) > max) qtyInput.value = max;
      }

      // Keep the URL in sync so the variant is shareable
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    }

    $$('input[data-opt-index]', form).forEach(i => i.addEventListener('change', update));

    // Quantity stepper
    $('#QtyMinus') && $('#QtyMinus').addEventListener('click', () => {
      qtyInput.value = Math.max(1, parseInt(qtyInput.value, 10) - 1);
    });
    $('#QtyPlus') && $('#QtyPlus').addEventListener('click', () => {
      const max = parseInt(qtyInput.max, 10) || 99;
      qtyInput.value = Math.min(max, parseInt(qtyInput.value, 10) + 1);
    });

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (errBox) { errBox.hidden = true; errBox.textContent = ''; }
      addBtn.disabled = true;
      const prev = addTxt.textContent;
      addTxt.textContent = 'Ajout…';
      try {
        await addToCart(idField.value, parseInt(qtyInput ? qtyInput.value : 1, 10));
        addTxt.textContent = 'Ajouté ✓';
        openDrawer();
        setTimeout(() => { addTxt.textContent = prev; addBtn.disabled = false; }, 1400);
      } catch (err) {
        if (errBox) { errBox.textContent = err.message; errBox.hidden = false; }
        addTxt.textContent = prev;
        addBtn.disabled = false;
      }
    });

    update();
  }

  function initModal() {
    const modal = $('#SizeGuide');
    const overlay = $('#SizeGuideOverlay');
    if (!modal) return;
    const open = () => {
      modal.setAttribute('aria-hidden', 'false');
      overlay && overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
    };
    const close = () => {
      modal.setAttribute('aria-hidden', 'true');
      overlay && overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');
    };
    $$('[data-size-guide-open]').forEach(b => b.addEventListener('click', open));
    $('#SizeGuideClose') && $('#SizeGuideClose').addEventListener('click', close);
    overlay && overlay.addEventListener('click', close);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  }

  /* ============================================================
     COLLECTION — fast filtering + infinite scroll
     ============================================================ */

  function initFilters() {
    const bar = $('#FilterBar');
    if (!bar) return;

    function submit() {
      const url = new URL(window.location.href);
      // wipe existing filter params
      Array.from(url.searchParams.keys())
        .filter(k => k.indexOf('filter.') === 0)
        .forEach(k => url.searchParams.delete(k));
      // rebuild from checked chips (name comes straight from Shopify param_name)
      $$('input[data-filter]:checked', bar).forEach(i => url.searchParams.append(i.name, i.value));
      const price = $('#PriceSelect', bar);
      if (price && price.value) {
        const [min, max] = price.value.split('-');
        if (min) url.searchParams.set('filter.v.price.gte', min);
        if (max) url.searchParams.set('filter.v.price.lte', max);
      }
      url.searchParams.delete('page');
      window.location.href = url.toString();
    }

    $$('input[data-filter]', bar).forEach(i => i.addEventListener('change', submit));
    const price = $('#PriceSelect', bar);
    price && price.addEventListener('change', submit);
    const sort = $('#SortSelect', bar);
    sort && sort.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  function initInfiniteScroll() {
    const grid = $('#ProductGrid');
    const btn = $('#LoadMore');
    if (!grid || !btn) return;

    let loading = false;
    let nextUrl = btn.dataset.nextUrl || '';

    async function load() {
      if (loading || !nextUrl) return;
      loading = true;
      btn.innerHTML = '<span class="spinner"></span>';
      try {
        const res = await fetch(nextUrl);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newCards = doc.querySelectorAll('#ProductGrid > .card');
        newCards.forEach(c => grid.appendChild(document.importNode(c, true)));
        const newBtn = doc.querySelector('#LoadMore');
        nextUrl = newBtn ? newBtn.dataset.nextUrl : '';
        if (nextUrl) {
          btn.textContent = 'Charger plus';
          btn.disabled = false;
        } else {
          btn.closest('.loadmore-wrap').remove();
        }
      } catch (e) {
        btn.textContent = 'Réessayer';
      }
      loading = false;
    }

    btn.addEventListener('click', load);

    // Auto-load when the button scrolls into view
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(entries => {
        entries.forEach(en => { if (en.isIntersecting) load(); });
      }, { rootMargin: '600px' });
      io.observe(btn);
    }
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    const els = $$('.reveal');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach(e => e.classList.add('reveal--in'));
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach(en => {
        if (en.isIntersecting) { en.target.classList.add('reveal--in'); obs.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach(e => io.observe(e));
  }

  /* ---------- Sticky header offset for announcement bar ---------- */
  function initBarOffset() {
    const bar = $('#AnnounceBar');
    const setH = () => {
      document.documentElement.style.setProperty('--bar-h', bar ? bar.offsetHeight + 'px' : '0px');
    };
    setH();
    window.addEventListener('resize', setH);
  }

  /* ---------- Boot ----------
     Global listeners are delegated on `document`, so they must be bound
     exactly once — re-running them would make a single click fire twice
     (and add an item to the cart twice). Section-scoped inits are safe to
     re-run when Shopify re-renders a section in the theme editor.        */
  let globalsBound = false;

  function bootGlobals() {
    if (globalsBound) return;
    globalsBound = true;
    initBarOffset();
    initNav();
    initSearch();
    initCart();
    initModal();
  }

  function bootSections() {
    initShuffle();
    initRails();
    initAutoScroll();
    initGallery();
    initProductForm();
    initFilters();
    initInfiniteScroll();
    initReveal();
  }

  function boot() { bootGlobals(); bootSections(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders a section: only re-init section-scoped behaviour
  document.addEventListener('shopify:section:load', bootSections);

  window.InexorableTheme = {
    refreshCart, openDrawer, closeDrawer, toast, money,
    initShuffle, initRails
  };
})();
